import { useState, useEffect, useCallback, useMemo } from "react";
import { X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiService } from "@/lib/api";
import type { HealthMetric } from "@/types/tracker";
import type { User } from "@/types";
import type { TimePeriod } from "@/components/charts";
import type { LucideIcon } from "lucide-react";

// Components
import { TrackerTimeControls } from "./TrackerTimeControls";
import {
  BloodGlucoseFilters,
  BloodPressureToggle,
} from "./TrackerMetricFilters";
import { BloodGlucoseLegend, BloodPressureLegend } from "./TrackerLegend";
import { TrackerDataList } from "./TrackerDataList";
import { StatsOverview } from "./StatsOverview";

// Chart components
import {
  BloodGlucoseChart,
  BloodPressureChart,
  HeartRateChart,
  WaistChart,
  HipChart,
  WeightChart,
  RespiratoryRateChart,
  TemperatureChart,
  getDateRange,
  getPeriodBounds,
  normalizeDateForPeriod,
} from "@/components/charts";

// Types
import type {
  BloodGlucoseReading,
  BloodPressureResponse,
  HeartRateResponse,
  WaistSizeResponse,
  HipSizeResponse,
  WeightResponse,
  RespiratoryRateResponse,
} from "@/types/tracker";

interface TrackerDetailPanelProps {
  metric: HealthMetric | null;
  metricIcon: LucideIcon;
  metricColor: string;
  selectedPatient?: User | null;
  onAddReading?: () => void;
  onDeleteReading?: (readingId: string) => Promise<void>;
  onClose?: () => void;
}

export const TrackerDetailPanel = ({
  metric,
  metricIcon: Icon,
  metricColor,
  selectedPatient,
  onAddReading,
  onDeleteReading,
  onClose,
}: TrackerDetailPanelProps) => {
  // Time state
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>("day");
  const [currentDate, setCurrentDate] = useState(() =>
    normalizeDateForPeriod("day", new Date())
  );

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Metric-specific state
  const [bloodGlucoseData, setBloodGlucoseData] = useState<
    BloodGlucoseReading[]
  >([]);
  const [bloodGlucoseFilters, setBloodGlucoseFilters] = useState<string[]>([
    "All",
  ]);
  const [bloodGlucoseYAxis, setBloodGlucoseYAxis] = useState({
    min: 0,
    max: 300,
    referenceRange: { min: 70, max: 140 },
  });

  const [bloodPressureData, setBloodPressureData] =
    useState<BloodPressureResponse | null>(null);
  const [isSystolic, setIsSystolic] = useState(true);
  const [systolicYAxis, setSystolicYAxis] = useState({
    min: 60,
    max: 240,
    referenceRange: { min: 90, max: 120 },
  });
  const [diastolicYAxis, setDiastolicYAxis] = useState({
    min: 30,
    max: 160,
    referenceRange: { min: 60, max: 80 },
  });

  const [heartRateData, setHeartRateData] = useState<HeartRateResponse | null>(
    null
  );
  const [heartRateYAxis, setHeartRateYAxis] = useState({
    min: 60,
    max: 200,
    referenceRange: { min: 70, max: 100 },
  });

  const [waistSizeData, setWaistSizeData] = useState<WaistSizeResponse | null>(
    null
  );
  const [hipSizeData, setHipSizeData] = useState<HipSizeResponse | null>(null);
  const [weightData, setWeightData] = useState<WeightResponse | null>(null);
  const [respiratoryRateData, setRespiratoryRateData] =
    useState<RespiratoryRateResponse | null>(null);
  const [temperatureData, setTemperatureData] = useState<any | null>(null);

  // Format date display
  const formatDateDisplay = useCallback(
    (period: TimePeriod): string => {
      switch (period) {
        case "day":
          return currentDate.toLocaleDateString("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });
        case "week": {
          const { start, end } = getPeriodBounds("week", currentDate);
          return `${start.toLocaleDateString("en-US", { day: "numeric", month: "short" })} - ${end.toLocaleDateString("en-US", { day: "numeric", month: "short" })}`;
        }
        case "month":
          return currentDate.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          });
        case "year":
          return currentDate.getFullYear().toString();
      }
    },
    [currentDate]
  );

  const handlePeriodChange = useCallback((period: TimePeriod) => {
    setSelectedPeriod(period);
    setCurrentDate(normalizeDateForPeriod(period, new Date()));
  }, []);

  const handleDateChange = useCallback(
    (date: Date) => {
      setCurrentDate(normalizeDateForPeriod(selectedPeriod, date));
    },
    [selectedPeriod]
  );

  // Fetch data functions
  const fetchBloodGlucoseData = useCallback(async () => {
    if (!selectedPatient?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = getDateRange(selectedPeriod, currentDate);
      const response = await apiService.getBloodGlucoseData(
        selectedPatient.id,
        startDate,
        endDate
      );
      setBloodGlucoseData(response.data.data);
      setBloodGlucoseYAxis(response.data.y_axis);
    } catch (err) {
      setError("Failed to load blood glucose data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedPatient?.id, selectedPeriod, currentDate]);

  const fetchBloodPressureData = useCallback(async () => {
    if (!selectedPatient?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = getDateRange(selectedPeriod, currentDate);
      const response = await apiService.getBloodPressureData(
        selectedPatient.id,
        startDate,
        endDate
      );
      setBloodPressureData(response.data);
      setSystolicYAxis(response.data.systolic.y_axis);
      setDiastolicYAxis(response.data.diastolic.y_axis);
    } catch (err) {
      setError("Failed to load blood pressure data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedPatient?.id, selectedPeriod, currentDate]);

  const fetchHeartRateData = useCallback(async () => {
    if (!selectedPatient?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = getDateRange(selectedPeriod, currentDate);
      const response = await apiService.getHeartRateData(
        selectedPatient.id,
        startDate,
        endDate
      );
      setHeartRateData(response.data);
      setHeartRateYAxis(response.data.y_axis);
    } catch (err) {
      setError("Failed to load heart rate data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedPatient?.id, selectedPeriod, currentDate]);

  const fetchWaistSizeData = useCallback(async () => {
    if (!selectedPatient?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = getDateRange(selectedPeriod, currentDate);
      const response = await apiService.getWaistSizeData(
        selectedPatient.id,
        startDate,
        endDate
      );
      setWaistSizeData(response.data);
    } catch (err) {
      setError("Failed to load waist size data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedPatient?.id, selectedPeriod, currentDate]);

  const fetchHipSizeData = useCallback(async () => {
    if (!selectedPatient?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = getDateRange(selectedPeriod, currentDate);
      const response = await apiService.getHipSizeData(
        selectedPatient.id,
        startDate,
        endDate
      );
      setHipSizeData(response.data);
    } catch (err) {
      setError("Failed to load hip size data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedPatient?.id, selectedPeriod, currentDate]);

  const fetchWeightData = useCallback(async () => {
    if (!selectedPatient?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = getDateRange(selectedPeriod, currentDate);
      const response = await apiService.getWeightData(
        selectedPatient.id,
        startDate,
        endDate
      );
      setWeightData(response.data);
    } catch (err) {
      setError("Failed to load weight data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedPatient?.id, selectedPeriod, currentDate]);

  const fetchRespiratoryRateData = useCallback(async () => {
    if (!selectedPatient?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = getDateRange(selectedPeriod, currentDate);
      const response = await apiService.getRespiratoryRateData(
        selectedPatient.id,
        startDate,
        endDate
      );
      setRespiratoryRateData(response.data);
    } catch (err) {
      setError("Failed to load respiratory rate data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedPatient?.id, selectedPeriod, currentDate]);

  const fetchTemperatureData = useCallback(async () => {
    if (!selectedPatient?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = getDateRange(selectedPeriod, currentDate);
      const response = await apiService.getTemperatureData(
        selectedPatient.id,
        startDate,
        endDate
      );
      setTemperatureData(response.data);
    } catch (err) {
      setError("Failed to load temperature data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedPatient?.id, selectedPeriod, currentDate]);

  // Fetch data when metric or period changes
  useEffect(() => {
    if (!metric || !selectedPatient?.id) return;

    switch (metric.id) {
      case "blood-glucose":
        fetchBloodGlucoseData();
        break;
      case "blood-pressure":
        fetchBloodPressureData();
        break;
      case "heart-rate":
        fetchHeartRateData();
        break;
      case "waist-size":
        fetchWaistSizeData();
        break;
      case "hip":
        fetchHipSizeData();
        break;
      case "weight":
        fetchWeightData();
        break;
      case "respiratory-rate":
        fetchRespiratoryRateData();
        break;
      case "temperature":
        fetchTemperatureData();
        break;
    }
  }, [
    metric?.id,
    selectedPeriod,
    currentDate,
    selectedPatient?.id,
    fetchBloodGlucoseData,
    fetchBloodPressureData,
    fetchHeartRateData,
    fetchWaistSizeData,
    fetchHipSizeData,
    fetchWeightData,
    fetchRespiratoryRateData,
    fetchTemperatureData,
  ]);

  // Transform data for list view
  const readingsForList = useMemo(() => {
    if (!metric) return [];

    switch (metric.id) {
      case "blood-glucose":
        return bloodGlucoseData.map((r) => ({
          id: r.id,
          date: r.date,
          time: r.time,
          value: r.value,
          unit: r.sample_unit,
          sampleType: r.sample_type?.name,
          label: r.label,
        }));
      case "blood-pressure": {
        if (!bloodPressureData) return [];
        const bpData = isSystolic
          ? bloodPressureData.systolic.data
          : bloodPressureData.diastolic.data;
        return bpData.map((r) => ({
          id: r.id,
          date: r.date,
          time: r.time,
          value: r.value,
          unit: metric.unit,
          label: r.label,
        }));
      }
      case "heart-rate":
        return (heartRateData?.data || []).map((r) => ({
          id: r.id,
          date: r.date,
          time: r.time,
          value: r.value,
          unit: metric.unit,
          label: r.label,
        }));
      case "waist-size":
        return (waistSizeData?.data || []).map((r) => ({
          id: r.id,
          date: r.date,
          time: r.time,
          value: r.value,
          unit: metric.unit,
          label: r.label,
        }));
      case "hip":
        return (hipSizeData?.data || []).map((r) => ({
          id: r.id,
          date: r.date,
          time: r.time,
          value: r.value,
          unit: metric.unit,
          label: r.label,
        }));
      case "weight":
        return (weightData?.data || []).map((r) => ({
          id: r.id,
          date: r.date,
          time: r.time,
          value: r.value,
          unit: metric.unit,
          label: r.label,
        }));
      case "respiratory-rate":
        return (respiratoryRateData?.data || []).map((r) => ({
          id: r.id,
          date: r.date,
          time: r.time,
          value: r.value,
          unit: metric.unit,
          label: r.label,
        }));
      case "temperature":
        return (temperatureData?.data || []).map((r: any) => ({
          id: r.id,
          date: r.date,
          time: r.time,
          value: r.value,
          unit: metric.unit,
          label: r.label,
        }));
      default:
        return [];
    }
  }, [
    metric,
    bloodGlucoseData,
    bloodPressureData,
    isSystolic,
    heartRateData,
    waistSizeData,
    hipSizeData,
    weightData,
    respiratoryRateData,
    temperatureData,
  ]);

  // Calculate stats
  const statsData = useMemo(() => {
    if (readingsForList.length === 0) return null;

    // Sort readings by date/time to ensure chronological order
    const sortedReadings = [...readingsForList].sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}`).getTime();
      const dateB = new Date(`${b.date}T${b.time}`).getTime();
      return dateA - dateB;
    });

    const values = sortedReadings.map((r) => r.value);
    const current = values[values.length - 1];
    const average =
      Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
    const min = Math.min(...values);
    const max = Math.max(...values);

    // Compare first third (older) vs last third (newer) for trend
    const third = Math.max(1, Math.floor(values.length / 3));
    const olderValues = values.slice(0, third);
    const newerValues = values.slice(-third);

    const olderAvg =
      olderValues.reduce((a, b) => a + b, 0) / olderValues.length || 0;
    const newerAvg =
      newerValues.reduce((a, b) => a + b, 0) / newerValues.length || 0;

    // Calculate change: positive = increasing, negative = decreasing
    const change = olderAvg > 0 ? ((newerAvg - olderAvg) / olderAvg) * 100 : 0;

    return {
      current,
      average,
      min,
      max,
      trend: {
        direction: (change > 3 ? "up" : change < -3 ? "down" : "neutral") as
          | "up"
          | "down"
          | "neutral",
        value: `${Math.abs(Math.round(change))}% vs period`,
      },
      chartData: values,
    };
  }, [readingsForList]);

  // Handle delete
  const handleDelete = async (index: number, readingId: string) => {
    if (!onDeleteReading) return;
    try {
      await onDeleteReading(readingId);
      // Refresh data
      switch (metric?.id) {
        case "blood-glucose":
          fetchBloodGlucoseData();
          break;
        case "blood-pressure":
          fetchBloodPressureData();
          break;
        case "heart-rate":
          fetchHeartRateData();
          break;
        case "waist-size":
          fetchWaistSizeData();
          break;
        case "hip":
          fetchHipSizeData();
          break;
        case "weight":
          fetchWeightData();
          break;
        case "respiratory-rate":
          fetchRespiratoryRateData();
          break;
        case "temperature":
          fetchTemperatureData();
          break;
      }
    } catch (err) {
      console.error("Failed to delete reading:", err);
    }
  };

  // Reset filters when metric changes
  useEffect(() => {
    if (metric?.id === "blood-glucose") {
      setBloodGlucoseFilters(["All"]);
    }
    // Reset period to day when metric changes
    setSelectedPeriod("day");
    setCurrentDate(new Date());
  }, [metric?.id]);

  // Empty state
  if (!metric) {
    return (
      <Card className="h-full flex flex-col border-dashed">
        <CardContent className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <span className="text-3xl">📈</span>
          </div>
          <h3 className="text-base font-semibold text-foreground mb-2">
            Select a Tracker
          </h3>
          <p className="text-sm text-muted-foreground max-w-xs">
            Click on a tracker card from the left panel to view detailed charts
            and analytics
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col border-border">
      {/* Header */}
      <CardHeader className="pb-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-lg"
              style={{ backgroundColor: `${metricColor}20` }}
            >
              <Icon className="w-5 h-5" style={{ color: metricColor }} />
            </div>
            <div>
              <CardTitle className="text-lg">{metric.title}</CardTitle>
              {selectedPatient && (
                <p className="text-xs text-muted-foreground">
                  {selectedPatient.first_name} {selectedPatient.last_name}
                </p>
              )}
            </div>
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-auto space-y-4 pt-0">
        {/* Chart Section with Integrated Controls */}
        <div className="bg-muted/30 rounded-xl border border-border overflow-hidden">
          {/* Chart Header with Time Controls */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
            <span className="text-sm font-medium text-foreground">
              {formatDateDisplay(selectedPeriod)}
            </span>
            <TrackerTimeControls
              selectedPeriod={selectedPeriod}
              onPeriodChange={handlePeriodChange}
              currentDate={currentDate}
              onDateChange={handleDateChange}
              metricColor={metricColor}
              variant="compact"
            />
          </div>

          {/* Chart */}
          <div className="h-[280px] p-4">
            {isLoading ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div
                    className="animate-spin rounded-full h-8 w-8 border-2 border-muted mx-auto mb-3"
                    style={{ borderTopColor: metricColor }}
                  />
                  <p className="text-sm text-muted-foreground">
                    Loading chart...
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="w-full h-full flex items-center justify-center text-destructive">
                <p className="text-sm">{error}</p>
              </div>
            ) : (
              <>
                {metric.id === "blood-glucose" && (
                  <BloodGlucoseChart
                    data={bloodGlucoseData}
                    selectedPeriod={selectedPeriod}
                    currentDate={currentDate}
                    yAxisConfig={bloodGlucoseYAxis}
                    selectedFilters={bloodGlucoseFilters}
                  />
                )}
                {metric.id === "blood-pressure" && (
                  <BloodPressureChart
                    data={bloodPressureData}
                    selectedPeriod={selectedPeriod}
                    currentDate={currentDate}
                    systolicYAxis={systolicYAxis}
                    diastolicYAxis={diastolicYAxis}
                    isSystolic={isSystolic}
                  />
                )}
                {metric.id === "heart-rate" && (
                  <HeartRateChart
                    data={heartRateData}
                    selectedPeriod={selectedPeriod}
                    currentDate={currentDate}
                    yAxisConfig={heartRateYAxis}
                  />
                )}
                {metric.id === "waist-size" && (
                  <WaistChart
                    data={waistSizeData}
                    selectedPeriod={selectedPeriod}
                    currentDate={currentDate}
                  />
                )}
                {metric.id === "hip" && (
                  <HipChart
                    data={hipSizeData}
                    selectedPeriod={selectedPeriod}
                    currentDate={currentDate}
                  />
                )}
                {metric.id === "weight" && (
                  <WeightChart
                    data={weightData}
                    selectedPeriod={selectedPeriod}
                    currentDate={currentDate}
                  />
                )}
                {metric.id === "respiratory-rate" && (
                  <RespiratoryRateChart
                    data={respiratoryRateData}
                    selectedPeriod={selectedPeriod}
                    currentDate={currentDate}
                  />
                )}
                {metric.id === "temperature" && (
                  <TemperatureChart
                    data={temperatureData}
                    selectedPeriod={selectedPeriod}
                    currentDate={currentDate}
                  />
                )}
              </>
            )}
          </div>

          {/* Legend - Inside chart card */}
          <div className="px-4 pb-3">
            {metric.id === "blood-glucose" && (
              <BloodGlucoseLegend isDayView={selectedPeriod === "day"} />
            )}
            {metric.id === "blood-pressure" && (
              <BloodPressureLegend isDayView={selectedPeriod === "day"} />
            )}
          </div>
        </div>

        {/* Stats Overview */}
        {statsData && (
          <StatsOverview
            current={statsData.current}
            average={statsData.average}
            min={statsData.min}
            max={statsData.max}
            unit={metric.unit}
            metricColor={metricColor}
            trend={statsData.trend}
            chartData={statsData.chartData}
          />
        )}

        {/* Metric-Specific Filters */}
        {metric.id === "blood-glucose" && selectedPeriod !== "day" && (
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Filter by Sample Type
            </p>
            <BloodGlucoseFilters
              selectedFilters={bloodGlucoseFilters}
              onFilterChange={setBloodGlucoseFilters}
              isDayView={false}
            />
          </div>
        )}

        {metric.id === "blood-pressure" && (
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              View Mode
            </p>
            <BloodPressureToggle
              isSystolic={isSystolic}
              onToggle={setIsSystolic}
            />
          </div>
        )}

        {/* Readings List */}
        <TrackerDataList
          readings={readingsForList}
          selectedPeriod={selectedPeriod}
          currentDate={currentDate}
          metricColor={metricColor}
          metricUnit={metric.unit}
          onDelete={handleDelete}
          formatDateDisplay={formatDateDisplay}
        />

        {/* Add Button */}
        {onAddReading && (
          <Button
            onClick={onAddReading}
            className="w-full h-11 font-medium"
            style={{ backgroundColor: metricColor }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Reading
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
